import { __rest } from "tslib";
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { BorderlessDropdown, SpacerVertical } from '@klarna/bubble-ui';
import ComboBox from '@bubble-contrib/molecule-combobox';
import { selectors as configSelectors } from 'mage-config';
import { FieldsetForObject, formUtils, HorizontalWrapper, MOET_CONSTANTS, TextArea, } from 'moet-core';
import { stepMap as widgetStepMap } from 'moet-test-utils/fixtures/widget-merchants';
import { validators } from '@/flow/validation';
import { CallToActionButton, ContainerTemplate, ArrayField } from '@/components';
import { getBackendFullUrlByRegion } from '@/flow/flow-visualizer/selectors';
import { createWidgetMerchant } from './merchant';
import { subTitle, title } from '@/moet-gui/constants';
import { AttachmentType } from '@/constants/documents';
const { WIDGET_TYPE } = MOET_CONSTANTS;
const componentPropTypes = {
    contractOnboardingTypes: PropTypes.array.isRequired,
    countries: PropTypes.array.isRequired,
    environment: PropTypes.string.isRequired,
    getBackendUrlByRegion: PropTypes.func.isRequired,
    platforms: PropTypes.array.isRequired,
    region: PropTypes.string.isRequired,
    setWidgetUrl: PropTypes.func.isRequired,
};
const FIELDS = {
    country: {
        component: ComboBox,
        size: 1,
        label: 'Country',
        validators: [
            {
                callback: validators.common.isRequired,
                errorMessage: 'Country is required',
            },
        ],
        extraProps: {
            borderless: true,
            showOptions: true,
            showSuggestions: true,
        },
    },
    region: {
        component: BorderlessDropdown,
        size: 1,
        label: 'Region',
        validators: [
            {
                callback: validators.common.isRequired,
                errorMessage: 'Region is required',
            },
        ],
    },
    widgetType: {
        component: BorderlessDropdown,
        size: 1,
        label: 'Widget type',
        validators: [
            {
                callback: validators.common.isRequired,
                errorMessage: 'Widget type is required',
            },
        ],
    },
    platform: {
        component: BorderlessDropdown,
        size: 1,
        label: 'Platform',
        validators: [],
        collectIf: ({ widgetType }) => [WIDGET_TYPE.UNMANAGED_KYC, WIDGET_TYPE.NATIVE_KYC].includes(widgetType.value),
    },
    contractOnboardingType: {
        component: BorderlessDropdown,
        size: 1,
        label: 'Contract Onboarding Type',
        validators: [],
        collectIf: ({ widgetType }) => [WIDGET_TYPE.BPC_POC].includes(widgetType.value),
    },
    prefillInformation: {
        component: TextArea,
        size: 1,
        label: 'Prefill Information (JSON)',
        validators: [
            {
                callback: (val) => {
                    try {
                        if (val === null || val === void 0 ? void 0 : val.length) {
                            JSON.parse(val);
                        }
                        return true;
                    }
                    catch (e) {
                        return false;
                    }
                },
                errorMessage: 'Invalid JSON',
            },
        ],
        collectIf: ({ widgetType }) => [WIDGET_TYPE.BPC_POC].includes(widgetType.value),
    },
    documentTypes: {
        component: ArrayField,
        size: 1,
        label: 'Document types',
        extraProps: {
            clearLabel: 'Clear all',
            selectedLabel: 'Selected document types',
        },
        collectIf: ({ widgetType }) => [WIDGET_TYPE.DOCUMENT_UPLOAD].includes(widgetType.value),
    },
};
class Component extends React.Component {
    constructor(props) {
        super(props);
        this.onSubmit = () => {
            const { fields } = this.state;
            formUtils.callToActionOnNextWrapper(fields, () => {
                const { environment, getBackendUrlByRegion } = this.props;
                const _a = formUtils.getMappedValues(fields), { documentTypes } = _a, formValues = __rest(_a, ["documentTypes"]);
                const params = Object.assign(Object.assign(Object.assign({}, formValues), (documentTypes && documentTypes.length ? { documentTypes: [documentTypes] } : {})), { 
                    // @ts-expect-error: FIXME
                    baseUrl: getBackendUrlByRegion(formValues.region), 
                    // @ts-expect-error: FIXME
                    country: formValues.country.item.key, environment: environment === 'development' ? 'local' : environment });
                this.setWidgetUrl(null);
                // @ts-expect-error: FIXME
                const flowType = params.widgetType === WIDGET_TYPE.BPC_POC ? 'BPC' : params.widgetType;
                this.setState({ loading: true, error: null }, () => createWidgetMerchant(params)
                    .then(url => this.setWidgetUrl(`${url.split('?')[0]}?flow_type=${flowType}`))
                    .catch(error => {
                    // eslint-disable-next-line no-console
                    console.error(error);
                    this.setState({ error });
                })
                    .then(() => this.setState({ loading: false })));
            });
        };
        this.setWidgetUrl = widgetUrl => {
            this.setState({ widgetUrl });
            this.props.setWidgetUrl(widgetUrl);
        };
        this.getFieldValue = ({ key, value }) => (['country'].includes(key) ? value.item.key : value);
        this.onChange = (key, value) => {
            const fieldValue = this.getFieldValue({ key, value });
            formUtils.onChangeFields(key, value, this.state, fields => {
                Object.keys(fields).forEach(fieldKey => {
                    const { collectIf = () => true } = FIELDS[fieldKey];
                    if (!collectIf(fields)) {
                        fields[fieldKey].value = undefined;
                    }
                });
                if (key === 'country') {
                    fields.platform.value = '';
                    fields.platform.extraProps.options = this.props.platforms
                        .filter(({ supportedCountries = [] }) => supportedCountries.includes(fieldValue))
                        .map(({ id: key, name: label }) => ({ label, key }));
                }
                this.setState({ fields });
            });
        };
        this.onBlur = () => undefined;
        const { countries, platforms, region, contractOnboardingTypes } = props;
        const preSelections = {
            country: '',
            widgetType: '',
            platform: '',
            contractOnboardingType: '',
            prefillInformation: '',
            region,
            variant: [],
        };
        const platformOptions = platforms && platforms.length
            ? platforms
                .filter(({ supportedCountries = [] }) => supportedCountries.includes(preSelections.country))
                .map(({ id: key, name: label }) => ({ label, key }))
            : [];
        const fields = this.makeForm({
            countries,
            platformOptions,
            preSelections,
            contractOnboardingOptions: contractOnboardingTypes,
        });
        this.state = {
            fields,
            initialState: { fields },
            loading: false,
            error: null,
            widgetUrl: null,
        };
    }
    makeForm({ countries, preSelections, platformOptions, contractOnboardingOptions }) {
        const FIELD_OPTIONS = {
            country: countries,
            widgetType: Object.keys(WIDGET_TYPE)
                .filter(t => t !== WIDGET_TYPE.RECOLLECTION_KYC)
                .map(k => ({ label: k, key: k })),
            platform: platformOptions,
            contractOnboardingType: contractOnboardingOptions,
            region: ['eu', 'us', 'ap'].map(e => ({ label: e, key: e.toLowerCase() })),
            documentTypes: Object.values(AttachmentType).map(aType => ({ key: aType, label: aType })),
        };
        return formUtils.initForm(Object.keys(FIELDS).reduce((acc, key) => {
            const _a = FIELDS[key], { collectIf: _collectIf, extraProps = {} } = _a, fieldProps = __rest(_a, ["collectIf", "extraProps"]);
            const options = FIELD_OPTIONS[key];
            return Object.assign(Object.assign({}, acc), { [key]: Object.assign(Object.assign({}, fieldProps), { extraProps: Object.assign(Object.assign({}, extraProps), { options }), value: preSelections[key], isInitialized: false, isValid: false }) });
        }, {}));
    }
    render() {
        const { error, fields, loading } = this.state;
        const templateProps = { error, title, subTitle };
        const fieldsToShow = Object.keys(fields).reduce((acc, key) => {
            const { collectIf = () => true } = FIELDS[key];
            return collectIf(fields) ? Object.assign(Object.assign({}, acc), { [key]: fields[key] }) : acc;
        }, {});
        return (React.createElement(ContainerTemplate, Object.assign({}, templateProps),
            React.createElement(FieldsetForObject, { isBorderless: true, tabIndexStart: 1, 
                // @ts-expect-error: FIXME
                loading: loading, name: "merchant-creator", fields: fieldsToShow, onChange: this.onChange, onBlur: this.onBlur }),
            React.createElement(SpacerVertical, { large: true }),
            React.createElement(HorizontalWrapper, { display: "inline-flex", alignItems: "center", justifyContent: "flex-end" },
                React.createElement(CallToActionButton, { loading: loading, onClick: this.onSubmit }, "Create"))));
    }
}
Component.propTypes = componentPropTypes;
Component.getVariantsForWidgetType = (widgetType, country) => {
    let options = [];
    if ([WIDGET_TYPE.MANAGED_RISK, WIDGET_TYPE.MANAGED_SME_RISK].includes(widgetType)) {
        // TODO variants for pre-assessment require updates on fixtures
        // options = preAssessmentStepMap[country] || preAssessmentStepMap.other || [];
    }
    else {
        options = widgetStepMap[country] || widgetStepMap.other || [];
    }
    return options.map(o => ({ label: o, key: o }));
};
export const MerchantCreator = connect(state => ({
    environment: configSelectors.environment(state),
    region: configSelectors.region(state),
    getBackendUrlByRegion: region => getBackendFullUrlByRegion(state)(region),
}))(Component);
